(() => {
  const qs = (scope, selector) => scope.querySelector(selector);
  const qsa = (scope, selector) => Array.from(scope.querySelectorAll(selector));

  const debounce = (fn, wait = 80) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => fn(...args), wait);
    };
  };

  const decodeImage = (slide) => {
    const img = slide?.querySelector('img');
    if (!img || !img.decode) return;

    try {
      img.decode();
    } catch (_) {}
  };

  const setupHero = (root) => {
    if (!root || root.dataset.haPdpHeroInitialized === 'true') return;

    const viewport = qs(root, '[data-ha-pdp-hero-viewport]');
    const track = qs(root, '[data-ha-pdp-hero-track]');
    const prevBtn = qs(root, '[data-ha-pdp-hero-prev]');
    const nextBtn = qs(root, '[data-ha-pdp-hero-next]');
    const progress = qs(root, '[data-ha-pdp-hero-progress]');

    if (!viewport || !track) return;

    const originalSlides = qsa(track, '[data-ha-pdp-hero-slide]');
    const realTotal = originalSlides.length;

    if (realTotal < 2) {
      root.dataset.haPdpHeroInitialized = 'true';
      if (progress) {
        progress.style.setProperty('--ha-pdp-progress', '1');
      }
      return;
    }

    /*
      Desktop shows 2 slides at once.
      We need enough clones on the right side so the final cloned "first" slide
      can become the left/active slide before the loop teleports back.
    */
    const lastClone = originalSlides[realTotal - 1].cloneNode(true);
    const firstClone = originalSlides[0].cloneNode(true);
    const secondClone = originalSlides[1 % realTotal].cloneNode(true);

    lastClone.setAttribute('data-ha-pdp-hero-clone', 'true');
    firstClone.setAttribute('data-ha-pdp-hero-clone', 'true');
    secondClone.setAttribute('data-ha-pdp-hero-clone', 'true');

    lastClone.setAttribute('aria-hidden', 'true');
    firstClone.setAttribute('aria-hidden', 'true');
    secondClone.setAttribute('aria-hidden', 'true');

    track.insertBefore(lastClone, originalSlides[0]);
    track.appendChild(firstClone);
    track.appendChild(secondClone);

    const slides = qsa(track, '[data-ha-pdp-hero-slide]');

    /*
      Internal index map:
      0 = cloned last slide
      1 through realTotal = real slides
      realTotal + 1 = cloned first slide
      realTotal + 2 = cloned second slide
    */
    const firstRealIndex = 1;
    const lastRealIndex = realTotal;
    const firstCloneIndex = realTotal + 1;

    let index = firstRealIndex;
    let scrollEndTimer = null;
    let isTeleporting = false;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchDeltaX = 0;
    let touchDeltaY = 0;
    let touchActive = false;

    const getRealIndex = (internalIndex = index) => {
      if (internalIndex === 0) return realTotal - 1;
      if (internalIndex > realTotal) return internalIndex - realTotal - 1;
      return internalIndex - 1;
    };

    const getProgressScale = (internalIndex = index) => {
      return (getRealIndex(internalIndex) + 1) / realTotal;
    };

    const updateProgress = (internalIndex = index) => {
      if (!progress) return;
      progress.style.setProperty('--ha-pdp-progress', String(getProgressScale(internalIndex)));
    };

    const getSlideLeft = (targetIndex) => {
      const slide = slides[targetIndex];
      return slide ? slide.offsetLeft : 0;
    };

    const getNearestIndex = () => {
      const currentLeft = viewport.scrollLeft;
      let nearest = 0;
      let nearestDistance = Infinity;

      slides.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - currentLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = i;
        }
      });

      return nearest;
    };

    const preloadNearby = () => {
      decodeImage(slides[index]);
      decodeImage(slides[index - 1]);
      decodeImage(slides[index + 1]);
      decodeImage(slides[index + 2]);
    };

    const jumpToIndex = (targetIndex) => {
      isTeleporting = true;
      index = targetIndex;

      viewport.scrollTo({
        left: getSlideLeft(targetIndex),
        behavior: 'auto'
      });

      updateProgress(index);
      preloadNearby();

      window.requestAnimationFrame(() => {
        isTeleporting = false;
      });
    };

    const scrollToIndex = (targetIndex, smooth = true) => {
      index = targetIndex;

      viewport.scrollTo({
        left: getSlideLeft(targetIndex),
        behavior: smooth ? 'smooth' : 'auto'
      });

      updateProgress(index);
      preloadNearby();
    };

    const normalizeLoopPosition = () => {
      /*
        If user moves left from first real slide:
        cloned last slide becomes visible, then teleport to real last slide.
      */
      if (index === 0) {
        jumpToIndex(lastRealIndex);
        return;
      }

      /*
        If user moves right from last real slide:
        cloned first slide becomes visible, then teleport to real first slide.
      */
      if (index >= firstCloneIndex) {
        jumpToIndex(firstRealIndex);
      }
    };

    const settleFromScroll = () => {
      if (isTeleporting) return;

      index = getNearestIndex();
      updateProgress(index);
      preloadNearby();
      normalizeLoopPosition();
    };

    const onScroll = () => {
      if (isTeleporting) return;

      window.clearTimeout(scrollEndTimer);

      const nearest = getNearestIndex();
      updateProgress(nearest);

      scrollEndTimer = window.setTimeout(settleFromScroll, 100);
    };

    const goPrev = (event) => {
      event?.preventDefault();
      scrollToIndex(index - 1, true);
    };

    const goNext = (event) => {
      event?.preventDefault();
      scrollToIndex(index + 1, true);
    };

    const goToMediaId = (mediaId) => {
      if (!mediaId) return;

      const targetSlide = slides.find((slide, i) => {
        if (i === 0 || i > realTotal) return false;
        return String(slide.dataset.mediaId) === String(mediaId);
      });

      if (!targetSlide) return;

      const targetIndex = slides.indexOf(targetSlide);
      if (targetIndex < 0) return;

      scrollToIndex(targetIndex, true);
    };

    prevBtn?.addEventListener('click', goPrev);
    nextBtn?.addEventListener('click', goNext);
    viewport.addEventListener('scroll', onScroll, { passive: true });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev(event);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext(event);
      }
    });

    viewport.addEventListener(
      'touchstart',
      (event) => {
        if (!event.touches || event.touches.length !== 1) return;

        const touch = event.touches[0];

        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchDeltaX = 0;
        touchDeltaY = 0;
        touchActive = true;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchmove',
      (event) => {
        if (!touchActive || !event.touches || event.touches.length !== 1) return;

        const touch = event.touches[0];

        touchDeltaX = touch.clientX - touchStartX;
        touchDeltaY = touch.clientY - touchStartY;
      },
      { passive: true }
    );

    viewport.addEventListener(
      'touchend',
      () => {
        if (!touchActive) return;

        const absX = Math.abs(touchDeltaX);
        const absY = Math.abs(touchDeltaY);
        const isHorizontalSwipe = absX > 32 && absX > absY * 1.2;

        if (isHorizontalSwipe) {
          if (touchDeltaX < 0) {
            goNext();
          } else {
            goPrev();
          }
        }

        touchActive = false;
        touchDeltaX = 0;
        touchDeltaY = 0;
      },
      { passive: true }
    );

    document.addEventListener('product:variant-change', (event) => {
      const variant = event.detail?.variant;
      const sectionId = event.detail?.sectionId;

      if (!variant || !variant.featured_media) return;
      if (sectionId && String(sectionId) !== String(root.dataset.sectionId)) return;

      goToMediaId(variant.featured_media.id);
    });

    const handleResize = debounce(() => {
      jumpToIndex(index);
    }, 90);

    window.addEventListener('resize', handleResize);

    window.requestAnimationFrame(() => {
      jumpToIndex(firstRealIndex);
    });

    root.dataset.haPdpHeroInitialized = 'true';
  };

  const init = (scope = document) => {
    qsa(scope, '[data-ha-pdp-hero-v2]').forEach(setupHero);
  };

  window.HAPDPHeroV2 = window.HAPDPHeroV2 || {};
  window.HAPDPHeroV2.init = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();


/* =========================================
   HERE & ALWAYS — Refill Kit Purchase Option Guard
   Production-Hardened Version

   Product ID: 8788271268022
   Variant IDs:
   - Small: 45092469178550
   - Big:   45092469211318

   Selling Plan ID: 6131155126

   Purpose:
   1) Keep native one-time default.
   2) Stop variant changes from accidentally toggling purchase options.
   3) Preserve customer choice if they intentionally choose auto-refill.
   4) Only inject selling_plan into the actual refill kit product form.
   5) Add state classes for more resilient CSS styling.
   ========================================= */

(function () {
  if (window.__haRefillPurchaseGuardLoaded) return;
  window.__haRefillPurchaseGuardLoaded = true;

  var HA_REFILL_PRODUCT_ID = '8788271268022';
  var HA_REFILL_SELLING_PLAN_ID = '6131155126';
  var HA_REFILL_VARIANT_IDS = ['45092469178550', '45092469211318'];

  var lastChosenMode = 'one_time';
  var isApplying = false;

  function getContainer() {
    return document.querySelector(
      '.selling_plan_app_container[data-product-id="' + HA_REFILL_PRODUCT_ID + '"]'
    );
  }

  function getVisibleSection() {
    var container = getContainer();
    if (!container) return null;

    return Array.prototype.slice
      .call(container.querySelectorAll('.selling_plan_theme_integration'))
      .find(function (section) {
        return !section.classList.contains('selling_plan_theme_integration--hidden');
      });
  }

  function getSectionIdFromInput(input) {
    if (!input || !input.name || !input.dataset.variantId) return null;

    return input.name
      .replace(/^purchaseOption_/, '')
      .replace(new RegExp('_' + input.dataset.variantId + '$'), '');
  }

  function getFormVariantValue(form) {
    if (!form) return '';

    var variantInput = form.querySelector('[name="id"]');
    if (!variantInput) return '';

    return String(variantInput.value || variantInput.getAttribute('value') || '');
  }

  function isRefillKitForm(form) {
    var container = getContainer();
    var variantValue = getFormVariantValue(form);

    return (
      form &&
      !form.closest('.rebuy-widget') &&
      (!container || !container.contains(form)) &&
      HA_REFILL_VARIANT_IDS.indexOf(variantValue) !== -1
    );
  }

  function getMainProductForms() {
    return Array.prototype.slice
      .call(document.querySelectorAll('form[action*="/cart/add"], form[id^="product-form"]'))
      .filter(isRefillKitForm);
  }

  function ensureSellingPlanOnForms() {
    getMainProductForms().forEach(function (form) {
      var input = form.querySelector('input[name="selling_plan"]');

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.appendChild(input);
      }

      input.value = HA_REFILL_SELLING_PLAN_ID;
    });
  }

  function removeSellingPlanFromForms() {
    getMainProductForms().forEach(function (form) {
      form.querySelectorAll('input[name="selling_plan"]').forEach(function (input) {
        input.remove();
      });
    });
  }

  function updateContainerStateClasses() {
    var container = getContainer();
    if (!container) return;

    container.classList.toggle('ha-refill--subscription', lastChosenMode === 'subscription');
    container.classList.toggle('ha-refill--one-time', lastChosenMode !== 'subscription');
  }

  function callOneTime(oneTimeInput) {
    if (
      oneTimeInput &&
      typeof window.handleOneTimePurchaseSelection === 'function'
    ) {
      var variantId = oneTimeInput.dataset.variantId;
      var sectionId = getSectionIdFromInput(oneTimeInput);

      if (variantId && sectionId) {
        window.handleOneTimePurchaseSelection(variantId, sectionId, null);
      }
    }
  }

  function callSubscription(subscriptionInput) {
    if (
      subscriptionInput &&
      typeof window.handleSubscriptionSelection === 'function'
    ) {
      var variantId = subscriptionInput.dataset.variantId;
      var sectionId = getSectionIdFromInput(subscriptionInput);
      var sellingPlanId = subscriptionInput.dataset.sellingPlanId || HA_REFILL_SELLING_PLAN_ID;
      var variantPrice = subscriptionInput.dataset.variantPrice || '';
      var perDeliveryPrice = subscriptionInput.dataset.variantPerDeliveryPrice || variantPrice;

      if (variantId && sectionId && sellingPlanId) {
        window.handleSubscriptionSelection(
          variantId,
          sectionId,
          sellingPlanId,
          variantPrice,
          perDeliveryPrice,
          false,
          null
        );
      }
    }
  }

  function applyMode() {
    if (isApplying) return;

    var section = getVisibleSection();
    if (!section) {
      updateContainerStateClasses();
      return;
    }

    var oneTimeInput = section.querySelector('.delivery_value[data-radio-type="one_time_purchase"]');
    var subscriptionInput = section.querySelector('.delivery_value_hidden[data-radio-type="selling_plan"]');
    var frequencyInput = section.querySelector('.delivery_value_small[data-radio-type="selling_plan"]');
    var fauxSubscriptionRadio = section.querySelector('.easy_radio_design');

    if (!oneTimeInput || !subscriptionInput) {
      updateContainerStateClasses();
      return;
    }

    isApplying = true;

    if (lastChosenMode === 'subscription') {
      callSubscription(subscriptionInput);

      oneTimeInput.checked = false;
      oneTimeInput.removeAttribute('checked');

      subscriptionInput.checked = true;
      subscriptionInput.setAttribute('checked', 'checked');

      if (frequencyInput) {
        frequencyInput.checked = true;
        frequencyInput.setAttribute('checked', 'checked');
      }

      if (fauxSubscriptionRadio) {
        fauxSubscriptionRadio.classList.remove('unchecked');
      }

      ensureSellingPlanOnForms();
    } else {
      callOneTime(oneTimeInput);

      oneTimeInput.checked = true;
      oneTimeInput.setAttribute('checked', 'checked');

      subscriptionInput.checked = false;
      subscriptionInput.removeAttribute('checked');

      if (frequencyInput) {
        frequencyInput.checked = false;
        frequencyInput.removeAttribute('checked');
      }

      if (fauxSubscriptionRadio) {
        fauxSubscriptionRadio.classList.add('unchecked');
      }

      removeSellingPlanFromForms();
    }

    updateContainerStateClasses();

    window.setTimeout(function () {
      isApplying = false;
    }, 80);
  }

  function applyAfterVariantChange() {
    window.setTimeout(applyMode, 80);
    window.setTimeout(applyMode, 220);
    window.setTimeout(applyMode, 450);
    window.setTimeout(applyMode, 750);
  }

  function capturePurchaseChoice(event) {
    var container = getContainer();
    if (!container || !event.target || !container.contains(event.target)) return;

    if (event.target.closest('.subscription-options')) {
      lastChosenMode = 'subscription';
      window.setTimeout(applyMode, 80);
      window.setTimeout(applyMode, 220);
      return;
    }

    if (event.target.closest('.purchase-option')) {
      lastChosenMode = 'one_time';
      window.setTimeout(applyMode, 80);
      window.setTimeout(applyMode, 220);
    }
  }

  function detectVariantChange(event) {
    var container = getContainer();
    if (!event.target) return;

    /* Ignore clicks/changes inside the subscription widget */
    if (container && container.contains(event.target)) return;

    var isVariantChange =
      event.target.matches('[name="id"]') ||
      event.target.closest('[data-variant-id]') ||
      event.target.closest('[data-option-value]') ||
      event.target.closest('.variant-picker') ||
      event.target.closest('.product-form__input') ||
      event.target.closest('variant-selects') ||
      event.target.closest('variant-radios');

    if (!isVariantChange) return;

    applyAfterVariantChange();
  }

  function handleKnownVariantEvents() {
    applyAfterVariantChange();
  }

  function protectAddToCart() {
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;

        if (!form || !form.matches('form[action*="/cart/add"], form[id^="product-form"]')) return;
        if (!isRefillKitForm(form)) return;

        if (lastChosenMode === 'subscription') {
          ensureSellingPlanOnForms();
        } else {
          removeSellingPlanFromForms();
        }
      },
      true
    );

    document.addEventListener(
      'click',
      function (event) {
        var button = event.target && event.target.closest(
          'button[type="submit"], button[name="add"], .product-form__submit, .product-form__cart-submit, [data-add-to-cart], .btn-add-to-cart'
        );

        if (!button) return;

        var form = button.closest('form[action*="/cart/add"], form[id^="product-form"]');
        if (!form || !isRefillKitForm(form)) return;

        if (lastChosenMode === 'subscription') {
          ensureSellingPlanOnForms();
        } else {
          removeSellingPlanFromForms();
        }
      },
      true
    );
  }

  function init(attempt) {
    var container = getContainer();

    if (!container) {
      if (attempt < 20) {
        window.setTimeout(function () {
          init(attempt + 1);
        }, 150);
      }
      return;
    }

    document.addEventListener('pointerdown', capturePurchaseChoice, true);
    document.addEventListener('click', capturePurchaseChoice, true);

    document.addEventListener('change', detectVariantChange, true);
    document.addEventListener('click', detectVariantChange, true);

    document.addEventListener('variant:change', handleKnownVariantEvents, true);
    document.addEventListener('product:variant-change', handleKnownVariantEvents, true);
    document.addEventListener('theme:variant:change', handleKnownVariantEvents, true);

    protectAddToCart();

    lastChosenMode = 'one_time';
    updateContainerStateClasses();

    window.setTimeout(applyMode, 250);
    window.setTimeout(applyMode, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(0);
    });
  } else {
    init(0);
  }
})();


/* =========================================
   HERE & ALWAYS — Mobile Personalizer Focus Mode

   Purpose:
   On mobile only, when Zepto Personalizer is opened,
   hide the PDP title, price, tax/shipping note, reviews,
   and variant selectors so the engraving controls sit closer
   to the live preview.

   Restores the hidden PDP elements when the customer clicks
   Clear Choice / closes the personalizer state.
   ========================================= */

(function () {
  if (window.__haMobilePersonalizerFocusModeLoaded) return;
  window.__haMobilePersonalizerFocusModeLoaded = true;

  const MOBILE_QUERY = window.matchMedia('(max-width: 768px)');
  const BODY_CLASS = 'ha-pplr-mobile-open';

  function getPersonalizer() {
    return document.querySelector('.ha-pdp-hero-v2 .product-personalizer');
  }

  function getPersonalizerButton() {
    const personalizer = getPersonalizer();
    if (!personalizer) return null;

    return personalizer.querySelector(
      '.ptc_button, .pplr-c-button, .pplr-btn'
    );
  }

  function isVisible(el) {
    if (!el) return false;

    const style = window.getComputedStyle(el);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0' &&
      el.offsetParent !== null
    );
  }

  function isPersonalizerOpen() {
    const personalizer = getPersonalizer();
    const button = getPersonalizerButton();

    if (!personalizer || !button) return false;

    const buttonText = (button.textContent || '').trim().toLowerCase();
    const fieldsWrapper = personalizer.querySelector('.cstmfy_personalize_text');

    return (
      buttonText.includes('clear choice') ||
      isVisible(fieldsWrapper)
    );
  }

  function syncPersonalizerFocusMode() {
    if (!MOBILE_QUERY.matches) {
      document.body.classList.remove(BODY_CLASS);
      return;
    }

    document.body.classList.toggle(BODY_CLASS, isPersonalizerOpen());
  }

  document.addEventListener(
    'click',
    function (event) {
      const clickedPersonalizerButton = event.target.closest(
        '.ha-pdp-hero-v2 .product-personalizer .ptc_button, .ha-pdp-hero-v2 .product-personalizer .pplr-c-button, .ha-pdp-hero-v2 .product-personalizer .pplr-btn'
      );

      if (!clickedPersonalizerButton) return;

      window.setTimeout(syncPersonalizerFocusMode, 80);
      window.setTimeout(syncPersonalizerFocusMode, 250);
    },
    true
  );

  function initMobilePersonalizerFocusMode() {
    syncPersonalizerFocusMode();

    const personalizer = getPersonalizer();
    if (!personalizer) return;

    const observer = new MutationObserver(syncPersonalizerFocusMode);

    observer.observe(personalizer, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
      attributeFilter: ['class', 'style']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobilePersonalizerFocusMode);
  } else {
    initMobilePersonalizerFocusMode();
  }

  window.addEventListener('resize', syncPersonalizerFocusMode);
})();